import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, TrendingDown, Home, Newspaper } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

/**
 * Phase 2 — Custom 404 with a trading joke (item #19).
 * Replaces the bland default with a branded "stop loss" page.
 */
const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 — non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background bg-dots-pattern">
      <SEOHead title="Stop Loss Triggered — Page Not Found" />
      <Header />

      <main className="flex-1 container flex items-center justify-center py-16 md:py-24">
        <div className="max-w-2xl w-full text-center">
          {/* Faux trading chart */}
          <div className="relative mx-auto mb-8 max-w-md" aria-hidden>
            <svg
              viewBox="0 0 400 120"
              className="w-full h-auto"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Grid lines */}
              <line x1="0" y1="30" x2="400" y2="30" stroke="hsl(var(--border))" strokeDasharray="2 4" />
              <line x1="0" y1="60" x2="400" y2="60" stroke="hsl(var(--border))" strokeDasharray="2 4" />
              <line x1="0" y1="90" x2="400" y2="90" stroke="hsl(var(--border))" strokeDasharray="2 4" />
              {/* Chart line — uptrend then crash */}
              <path
                d="M 0 80 L 40 70 L 80 75 L 120 55 L 160 45 L 200 30 L 240 60 L 280 90 L 320 105 L 360 110 L 400 115"
                stroke="hsl(var(--destructive))"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Stop-loss horizontal line */}
              <line x1="0" y1="100" x2="400" y2="100" stroke="hsl(var(--wire-gold))" strokeWidth="1" strokeDasharray="4 3" />
              <text x="6" y="97" fontSize="9" fill="hsl(var(--wire-gold))" fontFamily="monospace">
                STOP LOSS
              </text>
              {/* Crash dot */}
              <circle cx="280" cy="90" r="4" fill="hsl(var(--destructive))" />
            </svg>
          </div>

          <p className="text-eyebrow text-destructive mb-3 flex items-center justify-center gap-1.5">
            <TrendingDown className="h-3.5 w-3.5" />
            Error 404
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4 leading-tight">
            You've hit a stop loss.
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-lg mx-auto">
            This page took an unexpected drawdown and got liquidated. Let's get you back to
            something profitable.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Button asChild size="lg" className="font-display">
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Back to homepage
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="font-display">
              <Link to="/news">
                <Newspaper className="h-4 w-4 mr-2" />
                Browse the wire
              </Link>
            </Button>
          </div>

          <button
            onClick={() => window.history.back()}
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Or close the position and go back
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
